import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'model/State';
import { fetchTaxRate } from 'modules/data/taxRates/actions';
import MACPageHeader from './components/macPageHeader';
import PaymentDetailsInfoSection from './components/PaymentDetailsInfoSection';
import RedirectToProductList from './components/redirectToProductList';
import { HeaderContainer, PageTitle } from './components/styledComponents';
import {
  getCurrentMACContactEmail,
  selectCountryByName,
  selectDetails,
  selectGetCustomerByEmail,
  selectIsSuspended,
  selectPostedCreditCardDetails,
  selectValidateCountryChange,
} from './duck/selectors';
import { fetchCountryByName } from './duck/actions/country';
import { route } from './duck/actions/landing';

import {
  postActivateSuspended,
  postReconcilableValidationResultAccounts,
} from './duck/actions/accountId';
import { fetchExternal, fetchValidateCountryChange } from './duck/actions/contact';
import { postNoteAccount } from './duck/actions/noteAccount';
import { MACCreditCardDetails } from './duck/api/storage';
import { Action, redirect } from 'redux-first-router';
import { UpdatePaymentDetailsForm } from './components/UpdatePaymentDetailsForm';

type SelectOption = {
  label: string;
  value: string;
};

export const getCountryByCountryName = (allCountries, countryName) => {
  const country = allCountries?.find(
    ({ displayName }) => displayName?.toLowerCase() === countryName?.toLowerCase(),
  );
  return (
    country && {
      label: country.displayName,
      value: country.countryIsoCode,
    }
  );
};

const getStateOptionFromStates = (states, state) => states.find(({ value }) => value === state);

const PaymentDetailsPage = () => {
  const dispatch = useDispatch();

  const defaultFormState = {
    firstName: '',
    lastName: '',
    companyName: '',
    address1: '',
    address2: '',
    city: '',
    country: { label: '', value: '' },
    state: { label: '', value: '' },
    postcode: '',
    taxId: '',
    vatResponsible: null,
  };
  const [billingDetailsForm, setBillingDetailsForm] = useState(defaultFormState);
  const [states, setStates] = useState<SelectOption[]>([]);
  const [isCountrySelectChanged, setIsCountrySelectChanged] = useState(false);
  const [shouldShowPaymentDetailsForm, setShouldShowPaymentDetailsForm] = useState(false);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const accountId = useSelector((state: State) => state.location.payload.accountId);
  const contactEmail = useSelector(getCurrentMACContactEmail) || '';
  const accountDetails = useSelector((state: State) => selectDetails(state, accountId));
  const customerDetails = useSelector(selectGetCustomerByEmail);
  const isAccountSuspended = useSelector(selectIsSuspended);
  const allCountries = useSelector((state: State) => state.data.countries.all?.result);
  const countryDetails = useSelector((state: State) =>
    selectCountryByName(state, billingDetailsForm.country.label),
  );
  const postedCreditCardDetails = useSelector(selectPostedCreditCardDetails);
  const selectValidateCountry = useSelector((state: State) =>
    selectValidateCountryChange(state, billingDetailsForm.country.label),
  );

  useEffect(() => {
    document.title = 'My Atlassian | Update Payment Details';
  }, [
    accountId,
    accountDetails,
    contactEmail,
    customerDetails,
    isAccountSuspended,
    allCountries,
    countryDetails,
    postedCreditCardDetails,
    selectValidateCountry,
  ]);

  useEffect(() => {
    if (billingDetailsForm.state.value) {
      dispatch(fetchTaxRate(billingDetailsForm.country.value, billingDetailsForm.state.value));
    }
  }, [billingDetailsForm.state.value]);

  useEffect(() => {
    if (billingDetailsForm.country.label && isCountrySelectChanged) {
      dispatch(fetchCountryByName(billingDetailsForm.country.label));
    }
  }, [billingDetailsForm.country.value, isCountrySelectChanged]);

  useEffect(() => {
    if (customerDetails[contactEmail]?.result) {
      dispatch(
        fetchCountryByName(customerDetails[contactEmail]?.result?.organisationDetails?.country),
      );
    }
  }, [customerDetails[contactEmail]]);

  useEffect(() => {
    if (customerDetails?.[contactEmail]?.result && allCountries) {
      const {
        contactDetails: { firstName, lastName },
        organisationDetails: {
          name: companyName,
          address1,
          address2,
          city,
          country,
          postcode,
          taxId,
          payingVatResponsible: vatResponsible,
        },
      } = customerDetails[contactEmail].result;
      setBillingDetailsForm({
        firstName,
        lastName,
        companyName,
        address1,
        address2,
        city,
        country: getCountryByCountryName(allCountries, country) || '',
        state: { label: '', value: '' },
        postcode,
        taxId,
        vatResponsible,
      });
    }
  }, [customerDetails, contactEmail, allCountries]);

  useEffect(() => {
    if (
      customerDetails?.[contactEmail]?.result &&
      countryDetails &&
      states.length &&
      !isCountrySelectChanged
    ) {
      setBillingDetailsForm({
        ...billingDetailsForm,
        state: getStateOptionFromStates(
          states,
          customerDetails[contactEmail].result.organisationDetails.state,
        ),
      });
      dispatch(fetchValidateCountryChange(billingDetailsForm.country.label));
    }
  }, [customerDetails, contactEmail, countryDetails, states.length, isCountrySelectChanged]);

  useEffect(() => {
    if (countryDetails?.states.length && billingDetailsForm.country) {
      setStates(countryDetails.states.map(({ name, code }) => ({ label: name, value: code })));
    }
  }, [countryDetails, billingDetailsForm.country.value]);

  useEffect(() => {
    if (accountDetails && contactEmail && customerDetails?.[contactEmail]?.result) {
      const mainTechAndBillContacts = accountDetails.contactRelationships.filter(
        ({ main, contact }) =>
          main &&
          contact?.contactDetails?.externalUuid ===
            customerDetails?.[contactEmail]?.result?.contactDetails?.externalUuid,
      );
      if (mainTechAndBillContacts.length === 2) {
        setShouldShowPaymentDetailsForm(true);
      }
      setEndDate(new Date(accountDetails.latestPeriod.endDate));
    }
  }, [accountDetails, contactEmail, customerDetails?.[contactEmail]?.result]);

  useEffect(() => {
    if (postedCreditCardDetails === '' && selectValidateCountry) {
      const currentContactDetails = customerDetails[contactEmail].result;
      const {
        type,
        maskedNumber,
        expiryMonth,
        expiryYear,
        name: ccName,
      } = MACCreditCardDetails().load();
      const noteAccountPayload = {
        author: `${billingDetailsForm.firstName} ${billingDetailsForm.lastName} OR ${currentContactDetails.contactDetails.displayName}`,
        body: `The payment method has been updated by end user - ${type} - ${maskedNumber} (${expiryMonth}/${expiryYear}) - ${ccName}`,
      };

      const updatedContact = {
        contactDetails: {
          ...currentContactDetails.contactDetails,
          firstName: billingDetailsForm.firstName,
          lastName: billingDetailsForm.lastName,
        },
        organisationDetails: {
          ...currentContactDetails.organisationDetails,
          name: billingDetailsForm.companyName,

          address1: billingDetailsForm.address1,
          address2: billingDetailsForm.address2,
          city: billingDetailsForm.city,
          country: billingDetailsForm.country.label,
          state: billingDetailsForm.state.value,
          postcode: billingDetailsForm.postcode,
          taxId: billingDetailsForm.taxId,
          payingVatResponsible: billingDetailsForm.vatResponsible,
        },
      };

      if (shouldShowPaymentDetailsForm) {
        dispatch(fetchExternal(updatedContact));
      }
      dispatch(postReconcilableValidationResultAccounts(selectValidateCountry));
      if (isAccountSuspended) {
        dispatch(postActivateSuspended(accountId));
      }
      dispatch(postNoteAccount(accountId, noteAccountPayload));
      dispatch(redirect(route() as Action));
    }
  }, [postedCreditCardDetails, selectValidateCountry]);

  return (
    <>
      <RedirectToProductList />
      <MACPageHeader />
      <HeaderContainer bottomLine flexDirectionColumn>
        <PageTitle>
          <FormattedMessage
            defaultMessage="Update Payment Details"
            id="mac.payment-details-page.header"
          />
        </PageTitle>
        <br />
        <p>
          <FormattedMessage
            defaultMessage="You can use this form to update the payment details associated with your account."
            id="mac.payment-details-page.content"
          />
        </p>
      </HeaderContainer>
      <PaymentDetailsInfoSection />
      <UpdatePaymentDetailsForm
        accountId={accountId}
        endDate={endDate}
        shouldShowPaymentDetailsForm={shouldShowPaymentDetailsForm}
        countryDetails={countryDetails}
        allCountries={allCountries}
        states={states}
        billingDetailsForm={billingDetailsForm}
        customerDetailsForContact={customerDetails?.[contactEmail]?.result}
        contactEmail={contactEmail}
        setBillingDetailsForm={setBillingDetailsForm}
        setIsCountrySelectChanged={setIsCountrySelectChanged}
        selectValidateCountry={selectValidateCountry}
      />
    </>
  );
};

export default PaymentDetailsPage;
